/**
 * StatusDistributionBar Component
 *
 * Animated segmented bar showing task status proportions.
 * Segments animate from 0 width to their target width on mount.
 *
 * @module pages/DashboardPage/components/StatusDistributionBar
 */

import { memo, useState, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';

import type { StatusDistributionBarProps } from './StatusDistributionBar.types';
import styles from './StatusDistributionBar.module.css';

/** Percentage multiplier for width calculation */
const PERCENTAGE_MULTIPLIER = 100;

/** Status keys used in the distribution bar */
type StatusKey = 'completed' | 'active' | 'delayed' | 'pending';

/** Segment color configuration — maps to Mantine CSS variables */
const SEGMENT_CONFIG: readonly {
  readonly key: StatusKey;
  readonly color: string;
  readonly bordered: boolean;
}[] = [
  { key: 'completed', color: 'var(--mantine-color-green-6)', bordered: false },
  { key: 'active', color: 'var(--mantine-color-blue-6)', bordered: false },
  { key: 'delayed', color: 'var(--mantine-color-red-5)', bordered: false },
  { key: 'pending', color: 'transparent', bordered: true },
];

/**
 * StatusDistributionBar displays a horizontal segmented bar
 * representing the proportion of each task status.
 */
export const StatusDistributionBar = memo(function StatusDistributionBar({
  pending,
  active,
  completed,
  delayed,
  total,
}: StatusDistributionBarProps) {
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      setIsAnimated(true);
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  const segments = useMemo(() => {
    if (total <= 0) return [];

    const values: Record<StatusKey, number> = {
      completed,
      active,
      delayed,
      pending,
    };

    return SEGMENT_CONFIG.filter((config) => values[config.key] > 0).map(
      (config) => ({
        key: config.key,
        color: config.color,
        bordered: config.bordered,
        widthPercent: (values[config.key] / total) * PERCENTAGE_MULTIPLIER,
      })
    );
  }, [completed, active, delayed, pending, total]);

  if (segments.length === 0) return null;

  return (
    <div className={styles.bar}>
      {segments.map((segment) => (
        <div
          key={segment.key}
          className={`${styles.segment}${segment.bordered ? ` ${styles.bordered}` : ''}`}
          style={
            {
              '--segment-width': isAnimated ? `${segment.widthPercent}%` : '0%',
              '--segment-color': segment.color,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
});

StatusDistributionBar.displayName = 'StatusDistributionBar';
